import type {
  FetchError,
  StoreVoiceMessageResponse,
  StoreVoiceOrderConfirmResponse,
  StoreVoiceOrderConfirmSelection,
  StoreVoiceOrderParseResponse
} from "@repo/api-sdk";
import sdk from "@repo/api-sdk";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { invalidateSellerCarts } from "../cart/cache";

/**
 * Client data layer for voice ordering (KAN-143) and voice messages
 * (KAN-192). Three mutations:
 *
 * - {@link useVoiceOrderParse} - uploads the recorded clip to
 *   `/store/voice-order/parse` (KAN-144) and returns the classified match list.
 *   Raced against a hard 30s client timeout so a stuck Deepgram+LLM+Typesense
 *   round-trip never spins the UI forever, while still allowing normal latency.
 * - {@link useVoiceOrderConfirm} - resolves the buyer's picks against live
 *   seller offers via `/store/voice-order/confirm` (KAN-147): `dry_run: true`
 *   for the seller-grouped review, `dry_run: false` to commit into the seller
 *   cart(s). A commit (only) invalidates the seller carts so the cart page and
 *   header badge refresh.
 * - {@link useLeaveVoiceMessage} - uploads the recorded clip to
 *   `/store/voice-order/messages` (KAN-192). Independent of the two above -
 *   never chained with `parse`/`confirm`, and does not touch the seller-cart
 *   cache (a voice message never lands in a cart).
 */

/**
 * Hard client-side timeout for the parse call (KAN-143). Deliberately generous:
 * a chained transcription + extraction + per-item catalog search on a full 60s
 * clip won't reliably finish in a few seconds, and a tight window would
 * spuriously fail a slow-but-successful parse.
 */
export const VOICE_ORDER_PARSE_TIMEOUT_MS = 30_000;

export function useVoiceOrderParse() {
  return useMutation<StoreVoiceOrderParseResponse, Error, Blob>({
    mutationFn: async (audio) => {
      const controller = new AbortController();
      const timeout = setTimeout(
        () => controller.abort(),
        VOICE_ORDER_PARSE_TIMEOUT_MS
      );
      try {
        return await sdk.store.voiceOrder.parse(audio, {
          signal: controller.signal
        });
      } finally {
        clearTimeout(timeout);
      }
    }
  });
}

export interface VoiceOrderConfirmVariables {
  selections: StoreVoiceOrderConfirmSelection[];
  /** `true` previews the diff (no cart mutation); `false` commits it. */
  dryRun: boolean;
}

export function useVoiceOrderConfirm() {
  const queryClient = useQueryClient();

  return useMutation<
    StoreVoiceOrderConfirmResponse,
    FetchError,
    VoiceOrderConfirmVariables
  >({
    mutationFn: ({ selections, dryRun }) =>
      sdk.store.voiceOrder.confirm(selections, dryRun),
    onSettled: (_data, _error, variables) => {
      // Only a commit lands items in carts; a dry-run preview mutates nothing,
      // so it must not churn the seller-cart cache mid-review.
      if (!variables.dryRun) {
        invalidateSellerCarts(queryClient);
      }
    }
  });
}

/**
 * Hard client-side timeout for leaving a voice message (KAN-192). Shorter
 * than {@link VOICE_ORDER_PARSE_TIMEOUT_MS} on purpose: a message upload is a
 * plain file POST with no chained transcription/extraction/catalog-search
 * latency behind it, so a stuck request should surface sooner than a stuck
 * multi-step parse is allowed to.
 */
export const VOICE_MESSAGE_TIMEOUT_MS = 15_000;

export function useLeaveVoiceMessage() {
  return useMutation<StoreVoiceMessageResponse, Error, Blob>({
    mutationFn: async (audio) => {
      const controller = new AbortController();
      const timeout = setTimeout(
        () => controller.abort(),
        VOICE_MESSAGE_TIMEOUT_MS
      );
      try {
        return await sdk.store.voiceOrder.leaveMessage(audio, {
          signal: controller.signal
        });
      } finally {
        clearTimeout(timeout);
      }
    }
  });
}
